import { forwardRef, type ReactNode } from 'react';

import noop from 'lodash/fp/noop';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Button } from 'Components/LegacyButton';
import { Typography } from 'Components/Typography';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const Root = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  min-height: ${legacyTheme.props.buttonHeight};
  padding: 0 72px;
  background-color: ${theme.colors.neutral[100]};

  ${legacyTheme.breakpoints.up('md')} {
    boxshadow: 0 -2px 3px 0 rgba(234, 234, 234, 0.5);
  }
`;

const Terms = styled(Typography)`
  margin-right: ${legacyTheme.spacing.s56};
`;

const StyledButton = styled(Button)`
  margin-left: auto;
`;

type ConsultationFooterProps = {
  buttonLabel: string;
  children?: ReactNode;
  className?: string;
  disabled: boolean;
  isSaving: boolean;
  submit?: () => void;
  terms?: React.ReactNode;
};

export const ConsultationFooter = forwardRef<HTMLDivElement, ConsultationFooterProps>(
  ({ buttonLabel, children, className, disabled, isSaving, submit = noop, terms }, ref) => {
    const { isDesktop } = useResponsiveVariant();

    return (
      <Root ref={ref} className={className}>
        {terms && (
          <Terms color="grey" variant="p3">
            {terms}
          </Terms>
        )}
        {children ?? (
          <StyledButton
            aria-label="Navigate to next screen"
            data-click="next"
            data-from="sticky-footer"
            disabled={disabled || isSaving}
            fixed={!isDesktop}
            isLoading={isSaving}
            onClick={submit}
            type="submit"
            variant="vert"
          >
            {buttonLabel}
          </StyledButton>
        )}
      </Root>
    );
  },
);

ConsultationFooter.displayName = 'ConsultationFooter';
